import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';

export default function PrivacyPolicy() {
    const navigate = useNavigate();

    return (
        <div className="page-container min-h-screen p-4 md:p-12 overflow-x-hidden">
            <div className="fade-in max-w-[800px] mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                        className="text-gold hover:text-gold/80 hover:bg-gold/10"
                    >
                        <ChevronLeft size={24} />
                    </Button>
                    <h1 className="gold-text text-3xl md:text-4xl font-bold uppercase tracking-tighter">
                        Privacy Policy
                    </h1>
                </div>

                <div className="mystical-card p-6 md:p-10 text-gray-300 leading-relaxed space-y-6 text-base md:text-lg">
                    <section>
                        <p className="font-semibold text-white">Effective Date: January 16th 2026</p>
                        <p className="mt-4">
                            OmniRead (“we,” “us,” or “our”) values your privacy. This Privacy Policy explains how we collect,
                            use, disclose, and safeguard your information when you use the OmniRead application,
                            website, and related services (collectively, the “Service”).
                        </p>
                        <p className="mt-4">
                            By using OmniRead, you agree to the collection and use of information in accordance with this
                            policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">1. INFORMATION WE COLLECT</h2>
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-bold text-white mb-1">Personal Information You Provide</h3>
                                <p>
                                    When you create an account or use OmniRead, we may collect your name, email address,
                                    authentication information (such as Google Sign-In credentials), and account preferences or
                                    settings.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-bold text-white mb-1">Content and Usage Data</h3>
                                <p>
                                    We may collect information related to your use of the Service, including reading preferences,
                                    saved content or reflections, and interaction data such as features used and session activity.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-bold text-white mb-1">Automatically Collected Information</h3>
                                <p>
                                    We may automatically collect device information (such as browser type and operating system),
                                    IP address, log data, and cookies or similar tracking technologies.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">2. HOW WE USE YOUR INFORMATION</h2>
                        <p>
                            We use the information we collect to provide, operate, and maintain OmniRead; authenticate
                            users (including via Google OAuth); personalize content and user experience; improve app
                            functionality and performance; communicate with you regarding updates or support; ensure
                            security and prevent fraud; and comply with legal obligations.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">3. GOOGLE AUTHENTICATION (OAUTH)</h2>
                        <p>
                            OmniRead uses Google Sign-In (OAuth) for secure authentication.
                        </p>
                        <p className="mt-4">
                            We only access basic profile information, such as name and email address, as permitted by
                            Google. We do not access your Google Drive, contacts, or other private Google data. Google
                            authentication data is used solely to create and manage your OmniRead account.
                        </p>
                        <p className="mt-4 italic text-sm">
                            OmniRead’s use of Google user data complies with the Google API Services User Data Policy,
                            including the Limited Use requirements.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">4. SHARING YOUR INFORMATION</h2>
                        <p>We do not sell your personal data.</p>
                        <p className="mt-4">
                            We may share information only with trusted service providers who help operate the Service
                            (such as hosting, analytics, or authentication providers); to comply with legal obligations or
                            enforce our terms; or to protect the rights, safety, and security of OmniRead and its users.
                        </p>
                        <p className="mt-4">
                            All third-party providers are required to protect your data and use it only for authorized
                            purposes.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">5. DATA SECURITY</h2>
                        <p>
                            We implement reasonable administrative, technical, and physical security measures to protect
                            your information. However, no method of transmission or storage is one hundred percent
                            secure, and we cannot guarantee absolute security.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">6. DATA RETENTION</h2>
                        <p>
                            We retain personal information only as long as necessary to provide the Service, comply with
                            legal obligations, resolve disputes, and enforce agreements.
                        </p>
                        <p className="mt-4">
                            You may request deletion of your account and associated data at any time.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">7. YOUR RIGHTS AND CHOICES</h2>
                        <p>
                            Depending on your location, you may have the right to access your personal data, correct
                            inaccurate information, request deletion of your data, or withdraw consent.
                        </p>
                        <p className="mt-4">
                            To exercise these rights, contact us at the email below.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">8. COOKIES AND TRACKING TECHNOLOGIES</h2>
                        <p>
                            OmniRead may use cookies or similar technologies to maintain user sessions, analyze usage
                            patterns, and improve functionality.
                        </p>
                        <p className="mt-4">
                            You can control cookie preferences through your browser settings.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">9. CHILDREN’S PRIVACY</h2>
                        <p>
                            OmniRead is not intended for children under the age of thirteen. We do not knowingly collect
                            personal information from children.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">10. CHANGES TO THIS PRIVACY POLICY</h2>
                        <p>
                            We may update this Privacy Policy from time to time. Any changes will be posted with an
                            updated effective date.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl md:text-2xl font-bold text-gold uppercase mb-4">11. CONTACT US</h2>
                        <p>If you have questions or concerns about this Privacy Policy, contact us at:</p>
                        <div className="mt-4 space-y-1">
                            <p><span className="text-white font-bold">Email:</span> <a href="mailto:manifestingbasics@gmail.com" className="text-gold hover:underline">manifestingbasics@gmail.com</a></p>
                            <p><span className="text-white font-bold">Company Name:</span> Manifesting Made Basic Lifestyle LLC</p>
                            <p><span className="text-white font-bold">Website:</span> <a href="https://omniread.ai" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://omniread.ai</a></p>
                        </div>
                    </section>
                </div>

                <div className="mt-12 text-center pb-20">
                    <p className="text-gray-500 text-sm">
                        &copy; {new Date().getFullYear()} OmniRead.AI. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
}
